/**
 * Contract mirror: keep in sync with services/ai/src/mastra/web-access/analyze-external-web-result.ts
 * Owner: AI service (analyzeExternalWeb tool output). FE mirrors for stream/UI state only.
 * Tests: services/ai/tests/web-access/analyze-external-web-result.test.ts
 * FE behavior: site-assistant-message-reducer.test.ts, history-message-hydrator.test.ts
 */
import { readBoolean, readRecord, readTrimmedString } from '@/lib/prop-utils';
import type { Message } from '@/types/assistant';

export const ANALYZE_EXTERNAL_WEB_TOOL_NAME = 'analyzeExternalWeb';

export function isAnalyzeExternalWebPageScope(input: Record<string, unknown> = {}): boolean {
  return readTrimmedString(input.scope) === 'page';
}

export function isSiteScopeWebAnalysisToolName(
  toolName?: string,
  input: Record<string, unknown> = {},
): boolean {
  if (toolName !== ANALYZE_EXTERNAL_WEB_TOOL_NAME) {
    return false;
  }

  return !isAnalyzeExternalWebPageScope(input);
}

function readAnalyzeExternalWebScopeFromOutput(rawOutput: unknown): 'page' | 'site' {
  const record = readRecord(rawOutput);
  if (record && readTrimmedString(record.scope) === 'page') {
    return 'page';
  }

  return 'site';
}

function hasSiteAnalysisResult(record: Record<string, unknown>): boolean {
  return record.analysis !== undefined || readTrimmedString(record.error) !== undefined;
}

/** True when the scrape finished — not Mastra's "Still analyzing…" string. */
export function isAnalyzeExternalWebComplete(rawOutput: unknown, scope?: 'page' | 'site'): boolean {
  if (rawOutput == null || typeof rawOutput === 'string') {
    return false;
  }

  const record = readRecord(rawOutput);
  if (!record) {
    return false;
  }

  if (readBoolean(record.success) === false) {
    return true;
  }

  if (readBoolean(record.success) !== true) {
    return false;
  }

  const resolvedScope = scope ?? readAnalyzeExternalWebScopeFromOutput(rawOutput);
  return resolvedScope === 'page' || hasSiteAnalysisResult(record);
}

/** Server-stamped on terminal analyzeExternalWeb payloads in stream and memory. */
export const ANALYZE_EXTERNAL_WEB_TERMINAL_PHASE = 'terminal' as const;

/** Authoritative terminal check: prefers server-stamped resultPhase, else completeness rules. */
export function isTerminalAnalyzeExternalWebResult(
  rawOutput: unknown,
  scope?: 'page' | 'site',
): boolean {
  const record = readRecord(rawOutput);
  if (!record) {
    return false;
  }

  if (readTrimmedString(record.resultPhase) === ANALYZE_EXTERNAL_WEB_TERMINAL_PHASE) {
    return true;
  }

  return isAnalyzeExternalWebComplete(rawOutput, scope);
}

export function isIncompleteAnalyzeExternalWebTool(message: Message): boolean {
  if (message.role !== 'tool' || message.toolName !== ANALYZE_EXTERNAL_WEB_TOOL_NAME) {
    return false;
  }

  if (message.toolError || message.backgroundTaskStatus === 'failed') {
    return false;
  }

  if (message.backgroundTaskStatus === 'completed') {
    return false;
  }

  const scope = isAnalyzeExternalWebPageScope(message.toolInput ?? {}) ? 'page' : 'site';
  return !isTerminalAnalyzeExternalWebResult(message.toolResult?.rawOutput, scope);
}

export function isFailedAnalyzeExternalWebTool(message: Message): boolean {
  if (message.role !== 'tool' || message.toolName !== ANALYZE_EXTERNAL_WEB_TOOL_NAME) {
    return false;
  }

  if (message.toolError || message.backgroundTaskStatus === 'failed') {
    return true;
  }

  return readAnalyzeExternalWebFailureMessage(message.toolResult?.rawOutput) !== undefined;
}

export function readAnalyzeExternalWebFailureMessage(rawOutput: unknown): string | undefined {
  const record = readRecord(rawOutput);
  if (!record || readBoolean(record.success) !== false) {
    return undefined;
  }

  return readTrimmedString(record.error) ?? "Couldn't review that website";
}
